import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { LoaderComponent } from '../../../../shared/components/ui/loader/loader.component';
import {
  IntakeParty,
  IntakePartyClinic,
  IntakePartyDentist,
  IntakePartyDuplicate,
  IntakePartyOutcome,
  IntakeShilaPo,
  IntakeShilaPoChoice,
  IntakeShilaPoList,
} from '../../../../shared/models/intake-party.model';
import { BroshService } from '../../../../shared/services/brosh.service';
import { SharedService } from '../../../../shared/services/shared.service';
import { shilaPoFilterText } from '../shila-po-dialog/shila-po-dialog.component';

// the rows each list renders; the search box narrows the rest
export const PARTY_LIST_LIMIT = 50;

// The clinics whose name, address or city holds the term, in the server's order (it puts the similar names first).
export function filterPartyClinics(clinics: IntakePartyClinic[], term: string): IntakePartyClinic[] {
  const wanted = term.trim().toLowerCase();
  return (clinics ?? []).filter((clinic) =>
    [clinic.clinicDescr, clinic.address, clinic.city].some((field) => (field ?? '').toLowerCase().includes(wanted))
  );
}

// The dentists whose name holds the term, in the server's order, with those of the clinic first. The server's list
// carries clinic ids and a similar-name flag the Shila dialog's Doctor rows do not, hence not groupDentists.
export function groupPartyDentists(
  dentists: IntakePartyDentist[],
  clinicId: number | null,
  term: string
): { atClinic: IntakePartyDentist[]; others: IntakePartyDentist[] } {
  const wanted = term.trim().toLowerCase();
  const matching = (dentists ?? []).filter((dentist) => (dentist.wholeName ?? '').toLowerCase().includes(wanted));
  const atClinic = (dentist: IntakePartyDentist): boolean => clinicId !== null && (dentist.clinicIds ?? []).includes(clinicId);
  return { atClinic: matching.filter(atClinic), others: matching.filter((dentist) => !atClinic(dentist)) };
}

// The clinic a PO of an unknown branch starts on: the order's clinic when it is a likely one, else the first likely one.
export function branchChoice(po: IntakeShilaPo, orderClinicId: number): number | 'NEW' {
  const likely = po.likelyClinicIds ?? [];
  return likely.includes(orderClinicId) ? orderClinicId : (likely[0] ?? 'NEW');
}

// "Not a duplicate" is kept per order in this browser, so that order's possible duplicates are not offered again.
// Without storage (blocked, private mode) it holds for the popup's life only.
const DUPLICATES_PASSED_KEY = 'brosh.intakeDuplicatesPassed.';

export function isDuplicatesPassed(orderId: number): boolean {
  try {
    return localStorage.getItem(DUPLICATES_PASSED_KEY + orderId) === '1';
  } catch {
    return false;
  }
}

export function rememberDuplicatesPassed(orderId: number): void {
  try {
    localStorage.setItem(DUPLICATES_PASSED_KEY + orderId, '1');
  } catch {
    // no storage: the popup's own flag still holds
  }
}

// The popup that asks the lab taking an imported order for the clinic, then the dentist, when they are not known yet.
// When the lab has orders that may be this one, it first offers to merge the case into one of them; with the clinic and
// dentist known, that offer is all it shows.
// Spec: brosh_alg_services_v2 docs/superpowers/specs/2026-10-28-intake-clinic-dentist-dialog-design.md.
@Component({
  selector: 'intake-party-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, LoaderComponent],
  templateUrl: './intake-party-dialog.component.html',
  styleUrls: ['./intake-party-dialog.component.scss'],
})
export class IntakePartyDialogComponent implements OnInit {
  @Input() orderId: number;
  @Input() orderClinicId: number;
  @Input() patientName: string | null = null;
  // rx-form is merging the case into an order: no other choice may be made meanwhile
  @Input() merging = false;
  // after each successful save
  @Output() changed = new EventEmitter<void>();
  // nothing is left to ask here; SHILA_DENTIST: the PO dialog's dentist question is open
  @Output() finished = new EventEmitter<'DONE' | 'SHILA_DENTIST'>();
  // the lab confirmed the case duplicates the order with this Meimad number. rx-form makes the merge, so the kept
  // order still opens when the popup is closed while the call runs.
  @Output() mergeInto = new EventEmitter<number>();

  readonly limit = PARTY_LIST_LIMIT;
  data: IntakeParty | null = null;
  step: 'duplicates' | 'clinic' | 'dentist' = 'clinic';
  // the lab said the case duplicates none of the listed orders: the list is not offered again
  duplicatesPassed = false;
  busy = false;
  loading = true;
  loadFailed = false;
  loadingShila = false;
  // both steps are asked: fixed on the first load, so the step strip stays once the clinic is answered
  twoSteps: boolean | null = null;
  filter = '';

  // step 1
  clinicTab: 'existing' | 'shila' | 'new' = 'existing';
  clinics: IntakePartyClinic[] = [];
  pickedClinicId: number | null = null;
  newClinic = { name: '', phone: '', address: '', city: '' };
  // the Shila tab: loaded the first time it opens, for this popup's life
  shila: IntakeShilaPoList | null = null;
  shilaPos: IntakeShilaPo[] = [];
  private shilaText = new Map<string, string>();
  pickedPo: IntakeShilaPo | null = null;
  branch: number | 'NEW' = 'NEW';

  // step 2
  dentistTab: 'existing' | 'new' | 'none' = 'existing';
  groups: { atClinic: IntakePartyDentist[]; others: IntakePartyDentist[] } = { atClinic: [], others: [] };
  pickedDoctorId: number | null = null;
  newDentist = { wholeName: '', phone: '' };

  constructor(
    private broshService: BroshService,
    private sharedService: SharedService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loadFailed = false;
    this.broshService.getIntakeParty(this.orderId).subscribe(
      (data) => this.apply(data),
      // postIntakeService shows the server's message
      () => {
        this.busy = false;
        this.loading = false;
        this.loadFailed = true;
      }
    );
  }

  private apply(data: IntakeParty): void {
    this.data = data;
    this.loading = false;
    this.busy = false;
    this.twoSteps ??= data.party.needsClinic && data.party.needsDentist;
    this.filter = '';
    this.pickedClinicId = null;
    this.pickedDoctorId = null;
    this.pickedPo = null;
    if (data.party.needsClinic) {
      this.step = 'clinic';
      this.clinicTab = data.clinic.shilaChain ? 'shila' : 'existing';
      // a 3Shape case's last clinic is offered as picked
      if (!data.clinic.placeholder && !data.clinic.shilaChain) {
        this.pickedClinicId = data.clinic.clinicId;
      }
      this.newClinic = {
        name: data.scan.practiceName ?? '',
        phone: '',
        address: data.scan.address ?? '',
        city: data.scan.city ?? '',
      };
      this.filterClinics();
      if (this.clinicTab === 'shila') {
        this.loadShila();
      }
    } else if (data.party.needsDentist) {
      this.step = 'dentist';
      this.dentistTab = 'existing';
      this.newDentist = { wholeName: data.scan.dentistName ?? '', phone: '' };
      this.groupDentists();
    } else if (!this.offersDuplicates(data)) {
      this.finished.emit(data.party.shilaDentist ? 'SHILA_DENTIST' : 'DONE');
      return;
    }
    // the step above, if any, waits behind the possible duplicates
    if (this.offersDuplicates(data)) {
      this.step = 'duplicates';
    }
  }

  private offersDuplicates(data: IntakeParty): boolean {
    return !!data.duplicates?.length && !this.duplicatesPassed && !isDuplicatesPassed(this.orderId);
  }

  // clinic and dentist are known: the popup is only the duplicates offer
  get duplicatesOnly(): boolean {
    return !!this.data && !this.data.party.needsClinic && !this.data.party.needsDentist;
  }

  // Not a duplicate: on to the clinic, or to the dentist when only the dentist is missing, else done.
  passDuplicates(): void {
    this.duplicatesPassed = true;
    rememberDuplicatesPassed(this.orderId);
    const party = this.data.party;
    if (party.needsClinic) {
      this.step = 'clinic';
    } else if (party.needsDentist) {
      this.step = 'dentist';
    } else {
      this.finished.emit(party.shilaDentist ? 'SHILA_DENTIST' : 'DONE');
    }
  }

  // the Meimad number only when the order is shown by another number
  showMeimad(duplicate: IntakePartyDuplicate): boolean {
    return String(duplicate.orderIdMmd) !== String(duplicate.displayId);
  }

  // Asks rx-form to merge the case into the order, once the app's confirm popup is approved.
  merge(duplicate: IntakePartyDuplicate): void {
    if (this.busy || this.merging) return;
    this.sharedService.emitPopupchReceivedEvent({
      title: this.translateService.instant('translate.intakeParty.mergeTitle', { displayId: duplicate.displayId }),
      subTitle: this.translateService.instant('translate.intakeParty.mergeConfirm'),
      icon: 'exclamation-triangle',
      approveLabel: 'translate.intakeParty.merge',
      keep: false,
      approve: () => this.mergeInto.emit(duplicate.orderIdMmd),
    });
  }

  setFilter(filter: string): void {
    this.filter = filter;
    if (this.step === 'dentist') {
      this.groupDentists();
    } else if (this.clinicTab === 'shila') {
      this.filterShila();
    } else {
      this.filterClinics();
    }
  }

  private filterClinics(): void {
    const shown = filterPartyClinics(this.data?.clinics ?? [], this.filter);
    const picked = this.pickedClinicId;
    if (picked === null || this.filter.trim()) {
      this.clinics = shown;
      return;
    }
    // the clinic already picked (a 3Shape case's last one) leads the list, so it is never hidden past the row limit
    const row = (this.data?.clinics ?? []).find((c) => c.clinicId === picked) ?? {
      clinicId: picked, clinicDescr: this.data.clinic.clinicDescr, address: null, city: null, similar: false };
    this.clinics = [row, ...shown.filter((c) => c.clinicId !== picked)];
  }

  private groupDentists(): void {
    const all = groupPartyDentists(this.data?.dentists ?? [], this.data?.pendingClinic ? null : this.data?.clinic.clinicId, this.filter);
    // the row limit covers both groups together
    const atClinic = all.atClinic.slice(0, PARTY_LIST_LIMIT);
    this.groups = { atClinic, others: all.others.slice(0, PARTY_LIST_LIMIT - atClinic.length) };
  }

  private filterShila(): void {
    const term = this.filter.trim().toLowerCase();
    this.shilaPos = (this.shila?.pos ?? []).filter((po) => this.shilaText.get(po.poNumber).includes(term));
  }

  setClinicTab(tab: 'existing' | 'shila' | 'new'): void {
    this.clinicTab = tab;
    this.filter = '';
    this.filterClinics();
    if (tab === 'shila') {
      this.loadShila();
    }
  }

  setDentistTab(tab: 'existing' | 'new' | 'none'): void {
    this.dentistTab = tab;
    this.filter = '';
    this.pickedDoctorId = null;
    this.groupDentists();
  }

  private loadShila(): void {
    if (this.shila) {
      this.filterShila();
      return;
    }
    this.loadingShila = true;
    this.broshService.getIntakeShilaPos(this.orderId).subscribe(
      (list) => {
      this.loadingShila = false;
      this.shila = list;
      this.shilaText = new Map(list.pos.map((po) => [po.poNumber, `${shilaPoFilterText(po)}\n${(po.clinicName ?? '').toLowerCase()}`]));
      this.filterShila();
      },
      () => (this.loadingShila = false)
    );
  }

  pickPo(po: IntakeShilaPo): void {
    this.pickedPo = po;
    this.branch = po.clinicId ? 'NEW' : branchChoice(po, this.orderClinicId);
  }

  get canSave(): boolean {
    if (this.busy || !this.data) return false;
    if (this.step === 'duplicates') return false;
    if (this.step === 'dentist') {
      switch (this.dentistTab) {
        case 'existing':
          return this.pickedDoctorId !== null;
        case 'new':
          return !!this.newDentist.wholeName.trim();
        default:
          return true;
      }
    }
    switch (this.clinicTab) {
      case 'existing':
        return this.pickedClinicId !== null;
      case 'shila':
        return !!this.pickedPo;
      default:
        return !!this.newClinic.name.trim();
    }
  }

  save(): void {
    if (!this.canSave) return;
    const call: Observable<{ outcome: string; dentist?: { outcome: string } | null }> = this.step === 'dentist' ? this.saveDentist() : this.saveClinic();
    this.busy = true;
    call.subscribe(
      (rep: { outcome: string; dentist?: { outcome: string } | null }) => {
        // every wait for Meimad reads the same, whether the clinic or the PO's dentist is the one waiting
        const waiting = rep.outcome === 'WAITING' || rep.dentist?.outcome === 'WAITING';
        this.sharedService.alertInfo(this.translateService.instant(waiting ? 'translate.intakeParty.waiting' : 'translate.intakeParty.set'));
        this.changed.emit();
        // busy stays until the reload is applied, so a second save cannot fire on the old picks
        this.load();
      },
      // postIntakeService shows the server's message
      () => (this.busy = false)
    );
  }

  private saveDentist(): Observable<IntakePartyOutcome> {
    if (this.dentistTab === 'existing') {
      return this.broshService.setIntakeDentist(this.orderId, { doctorId: this.pickedDoctorId });
    }
    if (this.dentistTab === 'none') {
      return this.broshService.setIntakeDentist(this.orderId, { noDentist: true });
    }
    const phone = this.newDentist.phone.trim();
    return this.broshService.setIntakeDentist(this.orderId, {
      newDentist: { wholeName: this.newDentist.wholeName.trim(), ...(phone ? { phone } : {}) },
    });
  }

  private saveClinic(): Observable<{ outcome: string }> {
    if (this.clinicTab === 'existing') {
      return this.broshService.setIntakeClinic(this.orderId, { clinicId: this.pickedClinicId });
    }
    if (this.clinicTab === 'new') {
      const { name, phone, address, city } = this.newClinic;
      return this.broshService.setIntakeClinic(this.orderId, {
        newClinic: { name: name.trim(), phone: phone.trim() || undefined, address: address.trim() || undefined, city: city.trim() || undefined },
      });
    }
    const choice: IntakeShilaPoChoice = { poNumber: this.pickedPo.poNumber };
    if (!this.pickedPo.clinicId) {
      if (this.branch === 'NEW') {
        choice.newBranch = true;
      } else {
        choice.clinicId = this.branch;
      }
    }
    return this.broshService.takeIntakeShilaPo(this.orderId, choice);
  }

  // a clinic or dentist picked here is kept for the source's next cases; no dentist is not
  get showRemember(): boolean {
    if (this.step === 'clinic') return this.data.remember.clinic;
    return this.step === 'dentist' && this.dentistTab !== 'none' && this.data.remember.dentist;
  }

  get saveLabel(): string {
    return this.step === 'clinic' && this.data?.party.needsDentist ? 'translate.intakeParty.next' : 'translate.intakeParty.save';
  }
}
